import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { LANGUAGE_CODES, getLanguageUrl } from '../config/languages';
import { LanguageContext } from '../contexts/LanguageContext';
import { ENVIRONMENT, SEO_ENVIRONMENT } from '../config/environment';

/**
 * 動的メタデータ管理カスタムフック
 * 言語切り替えに対応したSEO最適化を提供
 */
export const useMetadata = () => {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  /**
   * メタデータを設定する関数
   * @param {Object} metadata - メタデータオブジェクト
   */
  const setMetadata = metadata => {
    // HTML lang属性を更新
    document.documentElement.lang = metadata.lang || locale;

    // タイトルを更新
    if (metadata.title) {
      document.title = metadata.title;
    }

    // メタタグを更新する関数
    const updateMetaTag = (name, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(attribute, name);
        document.head.appendChild(metaTag);
      }

      metaTag.setAttribute('content', content);
    };

    // 基本メタタグ
    if (metadata.description) {
      updateMetaTag('description', metadata.description);
    }

    if (metadata.keywords) {
      updateMetaTag('keywords', metadata.keywords);
    }

    updateMetaTag('robots', metadata.robots || SEO_ENVIRONMENT.ROBOTS_CONTENT);

    // OGPタグ
    if (metadata.ogTitle) {
      updateMetaTag('og:title', metadata.ogTitle, true);
    }

    if (metadata.ogDescription) {
      updateMetaTag('og:description', metadata.ogDescription, true);
    }

    if (metadata.ogImage) {
      updateMetaTag('og:image', metadata.ogImage, true);
    }

    if (metadata.ogUrl) {
      updateMetaTag('og:url', metadata.ogUrl, true);
    }

    if (metadata.canonicalUrl) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = metadata.canonicalUrl;
    }

    // Twitter Card
    if (metadata.twitterCard) {
      updateMetaTag('twitter:card', metadata.twitterCard);
    }

    if (metadata.twitterTitle) {
      updateMetaTag('twitter:title', metadata.twitterTitle);
    }

    if (metadata.twitterDescription) {
      updateMetaTag('twitter:description', metadata.twitterDescription);
    }

    if (metadata.twitterImage) {
      updateMetaTag('twitter:image', metadata.twitterImage);
    }

    // hreflang設定
    if (metadata.disableHreflang) {
      removeHreflangTags();
    } else {
      updateHreflangTags();
    }
  };

  /**
   * 既存のhreflangタグを削除
   */
  const removeHreflangTags = () => {
    const existingHreflangTags = document.querySelectorAll(
      'link[rel="alternate"][hreflang]'
    );
    existingHreflangTags.forEach(tag => tag.remove());
  };

  /**
   * hreflangタグを更新
   */
  const updateHreflangTags = () => {
    removeHreflangTags();

    const baseUrl = ENVIRONMENT.SITE_URL;
    const languages = [
      ...LANGUAGE_CODES.map(code => ({
        code,
        url: getLanguageUrl(code, baseUrl),
      })),
      { code: 'x-default', url: baseUrl },
    ];

    languages.forEach(({ code, url }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = code;
      link.href = url;
      document.head.appendChild(link);
    });
  };

  /**
   * デフォルトメタデータを取得
   */
  const getDefaultMetadata = () => {
    const baseTitle = intl.formatMessage({
      id: locale === 'ja' ? 'meta.title.ja' : 'meta.title.en',
    });

    const description = intl.formatMessage({
      id: locale === 'ja' ? 'meta.description.ja' : 'meta.description.en',
    });

    const keywords = intl.formatMessage({
      id: locale === 'ja' ? 'meta.keywords.ja' : 'meta.keywords.en',
    });

    return {
      title: baseTitle,
      description,
      keywords,
      ogTitle: baseTitle,
      ogDescription: description,
      ogImage: `${ENVIRONMENT.SITE_URL}/ogp-image.jpg`,
      ogUrl: getLanguageUrl(locale, `${ENVIRONMENT.SITE_URL}/`),
      canonicalUrl: getLanguageUrl(locale, `${ENVIRONMENT.SITE_URL}/`),
      twitterCard: 'summary_large_image',
      twitterImage: `${ENVIRONMENT.SITE_URL}/ogp-image.jpg`,
      twitterTitle: baseTitle,
      twitterDescription: description,
    };
  };

  /**
   * ページ固有のメタデータを設定
   * @param {string} pageKey - ページキー（例: 'about', 'service'）
   * @param {Object} customMeta - カスタムメタデータ
   */
  const setPageMetadata = (pageKey, customMeta = {}) => {
    const defaultMeta = getDefaultMetadata();

    // ページ固有のタイトル
    const pageTitle = pageKey
      ? intl.formatMessage({ id: `meta.title.${pageKey}` })
      : defaultMeta.title;

    // ページ固有の説明
    const pageDescription = pageKey
      ? intl.formatMessage({ id: `meta.description.${pageKey}` })
      : defaultMeta.description;

    const metadata = {
      ...defaultMeta,
      title: pageTitle,
      description: pageDescription,
      ogTitle: pageTitle,
      ogDescription: pageDescription,
      twitterTitle: pageTitle,
      twitterDescription: pageDescription,
      ...customMeta,
    };

    setMetadata(metadata);
  };

  return { setMetadata, setPageMetadata, getDefaultMetadata };
};
